import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MapPin, Bell, ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  eventInfo, ecosystmAbout,
} from "../mock";
import ThemesCarousel from "../components/AgendaCarousel";
import Wordmark from "../components/Wordmark";
import Reveal from "../components/Reveal";

const homeBeliefs = [
  {
    title: "Why this Summit?",
    body:
      "AI is shaped across an ecosystem. Bringing together the leaders influencing policy, investment, research, technology, and enterprise adoption creates a perspective few rooms can match.",
    accent: "#FF243D",
  },
  {
    title: "Why now?",
    body:
      "AI will continue to evolve, but organisations cannot wait for certainty. The decisions made today will shape how AI is governed, adopted, and scaled across Asia for years to come.",
    accent: "#143DE5",
  },
  {
    title: "Why Ecosystm?",
    body:
      "For more than a decade, Ecosystm has convened Asia\u2019s leaders through independent research and executive communities. Every discussion is grounded in research, centred on practical decisions, and moderated for open exchange.",
    accent: "#22CC74",
  },
];

const Home = () => {
  return (
    <main className="text-white">
      {/* HERO — reduced top padding */}
      <section className="relative eco-grad-hero pt-[78px] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full bg-[#ff243d]/20 blur-[140px] aurora-pulse" />
        <div className="absolute -bottom-32 -left-40 w-[520px] h-[520px] rounded-full bg-[#143DE5]/20 blur-[140px] aurora-pulse" style={{ animationDelay: "2s" }} />

        <div className="max-w-[1320px] mx-auto px-5 sm:px-5 sm:px-6 lg:px-10 py-10 sm:py-12 md:py-16 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <Reveal className="lg:col-span-7" from="up">
              <div className="inline-flex items-center gap-2 sm:gap-3 text-xs sm:text-sm tracking-[0.2em] sm:tracking-[0.24em] uppercase text-white/90 mb-5 sm:mb-6 px-4 py-2 rounded-full border border-white/25 bg-white/10 backdrop-blur">
                <span className="live-dot shrink-0" />
                <span className="font-medium">An Ecosystm Summit</span>
              </div>
              <h1 className="font-display text-[2.25rem] xs:text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.05]">
                {eventInfo.title}
              </h1>
              <p className="font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl mt-2 sm:mt-3 leading-snug">
                <span className="text-gradient-brand">{eventInfo.subtitle}</span>
              </p>
              <p className="mt-6 sm:mt-7 text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed">{eventInfo.description}</p>
              <div className="mt-6 sm:mt-7 flex flex-wrap gap-3 sm:gap-6 text-white/80 text-xs sm:text-sm">
                <a href={eventInfo.mapUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white group">
                  <MapPin className="w-4 h-4 text-[#ff243d] shrink-0" />
                  <span className="link-underline">{eventInfo.venue}</span>
                  <ExternalLink className="w-3 h-3 opacity-60 group-hover:opacity-100 shrink-0" />
                </a>
              </div>
              <div className="mt-7 sm:mt-8 flex flex-wrap gap-3">
                <Link to="/registration">
                  <Button className="bg-[#ff243d] hover:bg-[#e01a32] text-white rounded-full h-12 px-6 sm:px-7 text-sm sm:text-base shadow-[0_8px_30px_rgba(255,36,61,0.35)] hover:shadow-[0_8px_40px_rgba(255,36,61,0.55)] transition-shadow">
                    <Bell className="w-4 h-4 mr-2" />Register Interest <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
                <Link to="/programme">
                  <Button variant="outline" className="rounded-full h-12 px-6 sm:px-7 text-sm sm:text-base border-white/40 bg-transparent text-white hover:bg-white hover:text-black">
                    Themes &amp; Format <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
              <p className="mt-5 text-[10px] sm:text-xs tracking-widest uppercase text-white/40">{eventInfo.status}</p>
            </Reveal>

            <Reveal className="lg:col-span-5" from="right" delay={150}>
              <div className="relative aspect-square rounded-2xl border-2 border-[#ff243d]/40 p-5 sm:p-6 bg-gradient-to-br from-white/[0.02] to-transparent shine">
                <div className="h-full w-full rounded-xl bg-gradient-to-br from-[#1a1a1a]/60 via-[#0a0a0a] to-black flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 opacity-50 aurora-pulse" style={{ backgroundImage: "radial-gradient(circle at 70% 30%, #ff243d 0%, transparent 45%), radial-gradient(circle at 20% 80%, #143DE5 0%, transparent 40%)" }} />
                  <div className="relative flex items-center justify-center w-full h-full p-6 sm:p-8">
                    <Wordmark size="xl" variant="light" layout="vertical" className="mx-auto w-[96%] sm:w-[90%] max-w-[420px] !h-auto object-center" />
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INTRODUCTION + ROOM IMAGE */}
      <section className="bg-white text-black py-16 md:py-24">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 lg:items-center">
            <Reveal className="lg:col-span-6 flex flex-col rounded-2xl overflow-hidden" from="up">
              <div className="relative group aspect-[16/10] sm:aspect-[2/1] overflow-hidden">
                <img
                  src="/images/capella-ballroom.jpg"
                  alt="Asia AI Alliance at Capella Singapore"
                  className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1200ms] group-hover:scale-[1.03]"
                  loading="lazy"
                />
              </div>
              <div className="bg-black text-white px-5 sm:px-6 py-5 sm:py-6">
                <p className="font-display text-lg sm:text-xl lg:text-2xl font-semibold leading-snug">
                  Because AI is shaped across the entire ecosystem, not in isolation.
                </p>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-6 flex flex-col justify-center gap-5 sm:gap-6 text-slate-700 text-base sm:text-[17px] leading-relaxed" from="right">
              <p>Asia AI Alliance is where Asia&apos;s AI agenda is debated, challenged, and shaped by the people driving it.</p>
              <p>{eventInfo.longDescription}</p>
              <blockquote className="border-l-2 border-[#ff243d] pl-5 italic text-slate-800">
                &ldquo;{eventInfo.quote.text}&rdquo;
                <footer className="mt-3 not-italic text-sm text-slate-500">&mdash; {eventInfo.quote.author}</footer>
              </blockquote>
              <Link to="/registration" className="pt-1">
                <Button className="bg-[#ff243d] hover:bg-[#e01a32] text-white rounded-full h-12 px-7"><Bell className="w-4 h-4 mr-2" />Register Interest</Button>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THREE BELIEFS — coloured accents per belief */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-6 lg:px-10">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold text-black mb-3">
              <span className="section-line" />The Right Leaders. The Right Moment.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {homeBeliefs.map((b, i) => (
              <Reveal key={i} delay={i * 120}>
                <div className="bg-white rounded-xl p-8 border border-slate-200 hover:-translate-y-1 transition-all duration-300 shine h-full relative overflow-hidden">
                  <div className="absolute -top-px left-0 h-1 w-24 rounded-r-full" style={{ background: b.accent }} />
                  <div className="font-display text-6xl leading-none opacity-25" style={{ color: b.accent }}>0{i + 1}</div>
                  <h3 className="font-display text-2xl font-semibold text-black mt-4">{b.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THEMES CAROUSEL */}
      <section className="eco-bg-deep text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute -top-40 -right-32 w-[520px] h-[520px] rounded-full bg-[#143DE5]/15 blur-[140px] aurora-pulse" />
        <div className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full bg-[#ff243d]/15 blur-[140px] aurora-pulse" style={{ animationDelay: "3s" }} />
        <div className="max-w-[1320px] mx-auto px-5 sm:px-6 lg:px-10 relative">
          <Reveal>
            <ThemesCarousel />
          </Reveal>
          <Reveal delay={120} className="mt-12 text-center">
            <Link to="/programme"><Button className="bg-[#ff243d] hover:bg-[#e01a32] rounded-full h-12 px-8">All themes &amp; the full agenda <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          </Reveal>
        </div>
      </section>

      {/* VENUE */}
      <section className="bg-black text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="absolute top-1/2 left-1/4 w-[420px] h-[420px] rounded-full bg-[#22CC74]/12 blur-[120px] aurora-pulse" />

        <div className="max-w-[1320px] mx-auto px-5 sm:px-6 lg:px-10 relative">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-4">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold">
                <span className="section-line" />Venue
              </h2>
              <a href={eventInfo.mapUrl} target="_blank" rel="noreferrer" className="text-sm text-white/75 hover:text-white inline-flex items-center gap-2 link-underline">
                Open in Google Maps <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </Reveal>

          <Reveal from="left" className="mt-12">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 backdrop-blur shine max-w-2xl">
              <div className="font-display text-3xl font-semibold">{eventInfo.venue}</div>
              <div className="mt-1 text-white/70">{eventInfo.venueAddress}</div>

              <div className="mt-8 flex flex-wrap gap-3">
                <a href={eventInfo.mapUrl} target="_blank" rel="noreferrer">
                  <Button className="bg-[#ff243d] hover:bg-[#e01a32] rounded-full h-11 px-6">
                    <MapPin className="w-4 h-4 mr-2" />Open in Google Maps <ExternalLink className="w-4 h-4 ml-1.5" />
                  </Button>
                </a>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(eventInfo.venue + ", " + eventInfo.venueAddress)}`} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-full h-11 px-6 border-white/30 bg-transparent text-white hover:bg-white hover:text-black">
                    Get directions
                  </Button>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT ECOSYSTM */}
      <section className="bg-black text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute -bottom-20 right-0 w-[500px] h-[500px] rounded-full bg-[#22CC74]/10 blur-[140px] aurora-pulse" />
        <div className="max-w-[1320px] mx-auto px-5 sm:px-6 lg:px-10 relative">
          <Reveal from="left">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold">
              <span className="section-line" />{ecosystmAbout.headline}
            </h2>
            <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-3xl">{ecosystmAbout.body}</p>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
